import { Slot } from '@radix-ui/react-slot';
import { PropsWithChildren } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

/**
 * The list of navigation items.
 */
type StyledCollectionProps = PropsWithChildren<{
  open: boolean;
  asChild?: boolean;
}>;
export const StyledCollection = ({
  open,
  asChild,
  children,
  ...props
}: StyledCollectionProps) => {
  const Comp = asChild ? Slot : 'ul';
  return <Comp className={twJoin('m-0 block list-none p-0 lg:flex lg:justify-evenly', open && 'block lg:hidden', !open && 'hidden')} {...props} data-sentry-element="Comp" data-sentry-component="StyledCollection" data-sentry-source-file="navigation-collection.styles.tsx">
      {children}
    </Comp>;
};

/**
 * The nested list of navigation items.
 */
export const StyledSubCollection = ({
  open,
  asChild,
  children
}: StyledCollectionProps) => {
  const Comp = asChild ? Slot : 'ul';
  return <Comp className={twJoin('m-0 list-none p-0 lg:absolute lg:left-0 lg:right-0 lg:ml-auto lg:mr-auto lg:max-w-[1300px] lg:justify-between lg:bg-white lg:px-[3%] lg:py-0 lg:text-[1.25rem] lg:[box-shadow:3px_5px_5px_0_rgb(0_0_0_/_21%)] lg:group-hover:flex lg:group-focus-within:flex lg:group-hover:[&_&]:shadow-none lg:group-focus-within:[&_&]:shadow-none lg:[&_>_li]:flex-grow-[1] lg:[&_>_li]:basis-[100px]', open && 'block lg:hidden [&_>_*:last-child]:border-b-[1px] [&_>_*:last-child]:border-secondary-marine-100/50', !open && 'hidden')} data-sentry-element="Comp" data-sentry-component="StyledSubCollection" data-sentry-source-file="navigation-collection.styles.tsx">
      {children}
    </Comp>;
};
// export const StyledSubCollection = styled(StyledCollection)`
//   ${props =>
//     props.$open &&
//     css`
//       ${props => props.theme.navigationQuery.mobile} {
//         > *:last-child {
//           ${StyledItemMainChild} {
//             border-bottom: 1px solid ${props => props.theme.colors.darkBlue.o50};
//           }
//         }
//       }
//     `};

//   ${props => props.theme.navigationQuery.desktop} {
//     position: absolute;
//     left: 0;
//     right: 0;
//     padding: 0 3%;
//     justify-content: space-between;
//     font-size: 20px;
//     box-shadow: 3px 5px 5px 0 rgb(0 0 0 / 21%);
//     max-width: 1300px;
//     margin-left: auto;
//     margin-right: auto;
//     background: white;

//     > li {
//       flex-basis: 100px;
//       flex-grow: 1;
//     }
//   }
// `;

/**
 * A single navigation item.
 */
type StyledLiProps = PropsWithChildren<{
  mobile: boolean;
  desktop: boolean;
  asChild?: boolean;
}>;
export const StyledLi = ({
  mobile,
  desktop,
  asChild = false,
  children,
  ...props
}: StyledLiProps) => {
  const Comp = asChild ? Slot : 'li';
  return <Comp className={twMerge(twJoin('hidden', mobile && 'block lg:hidden', desktop && 'group lg:block'))} {...props} data-sentry-element="Comp" data-sentry-component="StyledLi" data-sentry-source-file="navigation-collection.styles.tsx">
      {children}
    </Comp>;
};

/**
 * The base styles of the navigation item.
 * Renders by default as an anchor tag.
 */
type StyledItemBaseProps = PropsWithChildren<{
  isActive: boolean;
  asChild?: boolean;
  className?: string;
}>;
export const StyledItemBase = ({
  isActive,
  className,
  asChild = false,
  children,
  ...props
}: StyledItemBaseProps) => {
  const Comp = asChild ? Slot : 'a';
  return <Comp className={twMerge(twJoin('flex w-full cursor-pointer appearance-none border-b-[1px] border-b-secondary-marine-100/60 bg-gray px-[2rem] py-[1.5rem] text-left text-[1.375rem] leading-[1.4em] text-marine-high-emphasis no-underline lg:border-0 lg:border-transparent lg:px-[1.25rem] lg:py-[0.875rem] lg:text-center lg:text-[1.5625rem] lg:leading-[1.1em] lg:group-hover:bg-white lg:group-focus-within:bg-white', className, isActive && 'border-b-0 bg-white lg:bg-primary-ocean-100'))} {...props} data-sentry-element="Comp" data-sentry-component="StyledItemBase" data-sentry-source-file="navigation-collection.styles.tsx">
      {children}
    </Comp>;
};
/**
 * The label of the navigation item.
 * It contains usually a custom icon (not the chevron) and the text.
 * Basic structure: [icon] [text]
 */
export const StyledNavigationLabel = ({
  asChild = false,
  ...props
}: any) => {
  const Comp = asChild ? Slot : 'div';
  return <Comp className="break-[break-word] flex flex-grow items-center gap-[1.5rem] lg:flex-col lg:gap-[0.5rem] [&_*:last-child]:flex-shrink lg:[&_*:last-child]:break-normal" {...props} data-sentry-element="Comp" data-sentry-component="StyledNavigationLabel" data-sentry-source-file="navigation-collection.styles.tsx"></Comp>;
};

/**
 * The `Sale` badge item.
 */
export const StyledItemBadgeSale = (props: any) => <StyledItemBase className="bg-signal-yellow-100" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemBadgeSale" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * The `SaleRose` badge item.
 */
export const StyledItemBadgeSaleRose = (props: any) => <StyledItemBase className="bg-signal-rose-100" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemBadgeSaleRose" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * The `Exclusive` badge item.
 */
export const StyledItemBadgeInclusive = (props: any) => <StyledItemBase className="bg-[#253E6A] text-white" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemBadgeInclusive" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * A simple item which can be used to render an image.
 */
export const StyledItemImage = (props: any) => <StyledItemBase className="justify-center bg-white" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemImage" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * The `Main` item of the navigation.
 */
export const StyledItemMain = (props: any) => <StyledItemBase className="bg-primary-ocean-100 lg:group-hover:[box-shadow:3px_5px_5px_0_rgb(0_0_0_/_21%)]" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemMain" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * The `Main` item's children of the navigation.
 */
export const StyledItemMainChild = (props: any) => <StyledItemBase className="h-full border-primary-ocean-20 bg-white px-[2rem] py-[1rem] text-[1.25rem] leading-[1.4em] lg:px-0 lg:py-[1.5rem] lg:text-[1.25rem] lg:hover:text-[#7390C5] lg:focus:text-[#7390C5]" {...props} data-sentry-element="StyledItemBase" data-sentry-component="StyledItemMainChild" data-sentry-source-file="navigation-collection.styles.tsx" />;

/**
 * The `Meta` item of the navigation.
 * Some implementation can be quite simple, as you can witness here.
 */
export const StyledItemMeta = StyledItemBase;